const ELM_NAME = 'dynamic-blog-posts';
const BLOG_POSTS_SELECTOR = '[data-blog-dynamic-posts]';
const BLOG_TOTALS_INFO_SELECTOR = '[data-blog-post-total]';
const LOAD_MORE_BUTTONS_SELECTOR = '[data-blog-load-more]';
const PAGINATION_SELECTOR = '[data-pagination]';
const WRAPPER_SELECTOR = '#main-blog-post-grid';
const PAGINATION_METER_SELECTOR = '[data-pagination-meter]';
const PAGINATION_METER_VALUE_SELECTOR = '[data-pagination-meter-value]';
const PAGINATION_METER_TEXT_SELECTOR = '[data-pagination-meter-text]';

const isValid = (value) => value !== undefined;

class DynamicBlogPosts extends HTMLElement {
  static get observedAttributes() {
    return ['data-page-config'];
  }

  /** @type {{ totalPages: Number, currentPage: Number, postsCount: Number, postsViewed: Number, sectionToFetch: String, firstViewedPost: String }} */
  pageConfig = {
    sectionToFetch: ELM_NAME,
  };

  setPageConfig() {
    const pageConfig = JSON.parse(this.getAttribute('data-page-config') || '{}');

    const { totalPages, currentPage, postsCount, sectionToFetch, postsViewed, firstViewedPost } =
      pageConfig;

    if (
      isValid(totalPages) &&
      isValid(currentPage) &&
      isValid(postsCount) &&
      isValid(postsViewed)
    ) {
      this.pageConfig = {
        totalPages: parseInt(totalPages),
        currentPage: parseInt(currentPage),
        postsCount: parseInt(postsCount),
        sectionToFetch,
        postsViewed: parseInt(postsViewed),
        firstViewedPost: parseInt(firstViewedPost),
      };

      return this.pageConfig;
    } else {
      console.error('Missing config value');
    }
  }

  setButtonDisabled(button) {
    button.disabled = true;
    button.classList.add('hidden');
    button.parentElement.classList.add('hidden');
  }

  setButtonEnabled(button) {
    button.disabled = false;
    button.classList.remove('hidden');
    button.parentElement.classList.remove('hidden');
  }

  setPaginationMeter() {
    const { postsViewed } = this.pageConfig;

    if (this.paginationMeterText && this.paginationMeterText.textContent) {
      let meterText = this.paginationMeterText.textContent;
      // Match all numbers in the text
      const match = this.paginationMeterText.textContent.match(/(\d+)/g);

      if (match && match.length === 2) {
        // Replace the first number with the number of posts viewed.
        meterText = meterText.replace(match[0], postsViewed);

        if (this.paginationMeterText) this.paginationMeterText.textContent = meterText;
      }
    }

    // Set the aria values and update the width of the meter
    if (this.paginationMeter && this.paginationMeterValue) {
      this.paginationMeter.setAttribute('aria-valuenow', postsViewed);
      this.paginationMeterValue.style.width = `${
        (postsViewed / this.pageConfig.postsCount) * 100
      }%`;
    }
  }

  handleLoadMoreClick = (event) => {
    const { currentTarget } = event;
    const loadType = currentTarget.dataset.blogLoadMore;

    this.isLoading();
    this.handleLoadMorePosts(loadType);
  };

  handleLoadMorePosts(loadType, previousPage) {
    const isPopState = previousPage !== undefined;
    const isNext = loadType == 'next';

    // Reset the page index if in popstate
    if (isPopState) {
      this.nextPageIndex = previousPage;
      this.previousPageIndex = previousPage;

      if (isNext) {
        this.nextPageIndex += 1;
        this.previousPageIndex = this.nextPageIndex;
      } else {
        this.previousPageIndex -= 1;
        this.nextPageIndex = this.previousPageIndex;
      }
    } else if (isNext) {
      this.nextPageIndex += 1;
    } else {
      this.previousPageIndex -= 1;
    }

    const pageToFetch = isNext ? this.nextPageIndex : this.previousPageIndex;

    const url = new URL(window.location.href);

    this.previousUrl = url;

    if (!url.searchParams.get('page')) {
      url.searchParams.append('page', `${pageToFetch}`);
    } else {
      url.searchParams.set('page', `${pageToFetch}`);
    }

    const paramsString = url.searchParams.toString();

    const fetchUrl = `${url.pathname}?${paramsString}`;

    const historyState = {
      page: pageToFetch,
    };

    this.renderPosts(fetchUrl, url, loadType, pageToFetch, historyState, isPopState);
  }

  async renderPosts(fetchUrl, newUrl, direction, pageToFetch, state, isPopState) {
    try {
      if (pageToFetch < 1) {
        this.isLoading(false);
        this.setButtonDisabled(this.loadMoreButtonPrevious);
        return;
      }

      if (pageToFetch > this.pageConfig.totalPages) {
        this.isLoading(false);
        this.setButtonDisabled(this.loadMoreButtonNext);
        return;
      }

      // Get the data we need
      const response = await fetch(fetchUrl);

      // Check response
      if (response.status != 200) {
        throw response;
      }

      // Get the text data
      const responseText = await response.text();

      // String to DOM
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      // Get the content from the section
      const newPosts = html.querySelector(BLOG_POSTS_SELECTOR)?.children;

      if (newPosts && newPosts.length) {
        const postsViewed = this.pageConfig.postsViewed + newPosts.length;

        if (isPopState) {
          // Replace the content during popstate
          this.blogPosts.replaceChildren(...newPosts);
        } else if (direction == 'next') {
          this.blogPosts.append(...newPosts);
        } else {
          this.blogPosts.prepend(...newPosts);
        }

        // Update the attribute (triggers attributeChangedCallback lifecycle method)
        this.setAttribute(
          'data-page-config',
          JSON.stringify({
            ...this.pageConfig,
            currentPage: pageToFetch,
            postsViewed,
          })
        );

        if (pageToFetch == this.pageConfig.totalPages) {
          this.setButtonDisabled(this.loadMoreButtonNext);

          // Re-enable the previous button if we got here from popstate and there are more posts to load
          if (this.pageConfig.postsViewed !== this.pageConfig.postsCount) {
            this.setButtonEnabled(this.loadMoreButtonPrevious);
          }
        } else if (pageToFetch == 1) {
          this.setButtonDisabled(this.loadMoreButtonPrevious);

          // Re-enable the next button if we got here from popstate and there are more posts to load
          if (this.pageConfig.postsViewed !== this.pageConfig.postsCount) {
            this.setButtonEnabled(this.loadMoreButtonNext);
          }
        } else if (pageToFetch > 2) {
          this.loadButtons.map(this.setButtonEnabled);
        }

        this.setPaginationMeter();
        this.setPostCountInfo(html);
      }

      // Don't push new state if we're in a popstate
      if (!isPopState) {
        history.pushState(state, null, newUrl);
      }

      this.isLoading(false);
    } catch (error) {
      console.error(error);
      this.isLoading(false);
    }
  }

  setPostCountInfo(html) {
    if (!this.blogTotalsInfo) return;

    const newBlogTotals = html.querySelector(BLOG_TOTALS_INFO_SELECTOR);

    if (!newBlogTotals) return;

    const firstViewedPost = this.blogTotalsInfo.querySelector('[data-first-viewed]');

    if (!firstViewedPost) return;

    const firstViewedPostValue = parseInt(firstViewedPost.textContent, 10);
    const newFirstViewedPostValue = parseInt(
      newBlogTotals.querySelector('[data-first-viewed]').textContent,
      10
    );
    const lastViewedPost = this.blogTotalsInfo.querySelector('[data-last-viewed]');
    const lastViewedPostValue = parseInt(lastViewedPost.textContent, 10);
    const newLastViewedPostValue = parseInt(
      newBlogTotals.querySelector('[data-last-viewed]').textContent,
      10
    );

    if (!this.maxPostsViewed) {
      this.maxPostsViewed = lastViewedPostValue;
    }

    if (newLastViewedPostValue > this.maxPostsViewed) {
      this.maxPostsViewed = newLastViewedPostValue;
    }

    firstViewedPost.textContent =
      newFirstViewedPostValue < firstViewedPostValue
        ? newFirstViewedPostValue
        : firstViewedPostValue;

    lastViewedPost.textContent = this.maxPostsViewed;
  }

  isLoading(state = true) {
    this.loadButtons.forEach((button) => {
      button.disabled = state;
      state ? button.classList.add('loading') : button.classList.remove('loading');
    });
  }

  handlePopState = (event) => {
    const { state } = event;

    if (state?.page !== undefined) {
      const previousPage = parseInt(this.previousUrl.searchParams.get('page'));
      console.log('previousPage', previousPage);

      const direction = state.page > previousPage ? 'next' : 'previous';

      this.handleLoadMorePosts(direction, previousPage);
    }
  };

  connectedCallback() {
    if (this.isConnected) {
      const pageConfig = this.setPageConfig();

      if (pageConfig) {
        this.previousPageIndex = pageConfig.currentPage;
        this.nextPageIndex = pageConfig.currentPage;
        this.blogPosts = document.querySelector(BLOG_POSTS_SELECTOR);
        this.loadButtons = [...this.querySelectorAll(LOAD_MORE_BUTTONS_SELECTOR)];

        this.blogTotalsInfo = document.querySelector(BLOG_TOTALS_INFO_SELECTOR);

        this.paginationMeter = this.querySelector(PAGINATION_METER_SELECTOR);
        this.paginationMeterText = this.querySelector(PAGINATION_METER_TEXT_SELECTOR);
        this.paginationMeterValue = this.querySelector(PAGINATION_METER_VALUE_SELECTOR);
        this.pagination = document.querySelector(`${WRAPPER_SELECTOR} ${PAGINATION_SELECTOR}`);
        this.previousUrl = new URL(window.location.href);

        // Check if we have a previous history object state
        const previousHistoryState = history.state?.constructor === Object ? history.state : {};

        // This ensures the user can click all the way back to initial page load
        history.replaceState(
          {
            ...previousHistoryState,
            page: pageConfig.currentPage,
          },
          null,
          window.location.href
        );

        window.addEventListener('popstate', this.handlePopState);

        this.loadButtons.forEach((button) => {
          const direction = button.dataset.blogLoadMore;

          if (direction == 'previous') {
            this.loadMoreButtonPrevious = button;
          } else if (direction == 'next') {
            this.loadMoreButtonNext = button;
          }
          button.addEventListener('click', this.handleLoadMoreClick);
        });

        this.setPaginationMeter();
      }
    }
  }

  attributeChangedCallback() {
    this.setPageConfig();
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.handlePopState);

    this.loadButtons.forEach((button) => {
      console.log('button', button);
      button.removeEventListener('click', this.handleLoadMoreClick);
    });
  }
}

if (!customElements.get(ELM_NAME)) {
  window.customElements.define(ELM_NAME, DynamicBlogPosts);
}
